import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Input, OnDestroy, Optional, Self } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-list-input',
  templateUrl: './list-input.component.html',
  styleUrls: ['./list-input.component.scss'],
})
export class ListInputComponent implements ControlValueAccessor, OnDestroy {
  @Input() placeholder: string = '';
  @Input() label?: string;

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public addOnBlur = true;
  public values: string[] = [];
  public inputControl = new FormControl('');

  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  private onChange = (value: string[]) => {};
  public onTouched = () => {};
  private destroy$ = new Subject<void>();

  // Getter to check if the field is required and has the required validator
  get isRequired(): boolean {
    if (this.ngControl?.control?.validator) {
      const validation = this.ngControl.control.validator(new FormControl(''));
      return !!validation?.['required'];
    }
    return false;
  }

  // Getter to check if the field is invalid and touched
  get hasError(): boolean {
    return !!(this.ngControl?.control?.invalid && this.ngControl?.control?.touched);
  }

  add(event: MatChipInputEvent): void {
    const value = event.value?.trim();

    if (value && this.isValidInteger(value)) {
      this.values.push(value);
      this.updateFormValue();
    }

    event.chipInput!.clear();
    this.inputControl.setValue('');
  }

  private isValidInteger(value: string): boolean {
    return /^\d+$/.test(value) && !isNaN(Number(value));
  }

  private updateFormValue(): void {
    this.onChange(this.values);
    this.onTouched();
  }

  // Handle keypress events to only allow integer input
  onKeyPress(event: KeyboardEvent): boolean {
    const key = event.key;

    // Allow control keys and shortcuts
    if (this.isControlKey(key, event)) {
      return true;
    }

    // Only allow numbers (0-9)
    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  private isControlKey(key: string, event: KeyboardEvent): boolean {
    // Allow: backspace, delete, tab, escape, enter
    const controlKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];

    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    const shortcuts = ['a', 'c', 'v', 'x'];

    return controlKeys.includes(key) || (event.ctrlKey && shortcuts.includes(key.toLowerCase()));
  }

  remove(value: string): void {
    const index = this.values.indexOf(value);
    if (index >= 0) {
      this.values.splice(index, 1);
      this.updateFormValue();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string[] | string | null): void {
    this.values = Array.isArray(value)
      ? [...value]
      : typeof value === 'string' && value
      ? [value]
      : [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
